import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';

import wedlyLogo from '../../assets/wedly-logo.png';

import css from './Loader.module.css';

const SIZE_CLASS = {
  small: css.sizeSmall,
  medium: css.sizeMedium,
  large: css.sizeLarge,
};

/**
 * Branded loading indicator using the Wedly logo (subtle pulse animation).
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className — merged onto root
 * @param {string?} props.rootClassName — merged onto root (overrides default root padding)
 * @param {'small'|'medium'|'large'} [props.size='medium'] — `small` for inline controls (~32px)
 * @param {number?} props.delay — if set, show loader only after this many ms
 * @param {string?} props.ariaLabel — screen reader label (defaults to shared loading string)
 */
const LoaderCore = ({ rootClassName, className, size = 'medium', ariaLabel }) => {
  const classes = classNames(css.root, SIZE_CLASS[size], rootClassName, className);
  return (
    <div
      className={classes}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={ariaLabel}
    >
      <img src={wedlyLogo} alt="" className={css.logo} draggable={false} />
    </div>
  );
};

const DelayedLoader = props => {
  const [visible, setVisible] = useState(false);
  const { delay = 600, ...rest } = props;

  useEffect(() => {
    const timer = globalThis.window?.setTimeout(() => setVisible(true), delay);
    return () => {
      if (timer) {
        globalThis.window?.clearTimeout(timer);
      }
    };
  }, [delay]);

  return visible ? <LoaderCore {...rest} /> : null;
};

const Loader = props => {
  const intl = useIntl();
  const { delay, ariaLabel: ariaLabelProp, size = 'medium', ...rest } = props;
  const ariaLabel =
    ariaLabelProp ?? intl.formatMessage({ id: 'IconSpinner.screenreader.loading' });

  return delay != null ? (
    <DelayedLoader delay={delay} ariaLabel={ariaLabel} size={size} {...rest} />
  ) : (
    <LoaderCore ariaLabel={ariaLabel} size={size} {...rest} />
  );
};

export default Loader;
